import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import SEO from '../components/SEO'

export default function ForespoergselPage() {
  const [searchParams] = useSearchParams()
  const prefillSag = searchParams.get('sag') || ''
  const prefillBaad = searchParams.get('baad') || ''

  const [formData, setFormData] = useState({
    navn: '',
    adresse: '',
    postnr: '',
    by: '',
    telefon: '',
    email: '',
    baadtype: '',
    maerke: '',
    pris: '',
    beskrivelse: prefillSag || prefillBaad ? `Jeg er interesseret i ${prefillBaad} (${prefillSag}).` : '',
    website_url: '', // Honeypot field
  })

  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrorMessage('')

    if (
      !formData.navn.trim() ||
      !formData.adresse.trim() ||
      !formData.postnr.trim() ||
      !formData.by.trim() ||
      !formData.telefon.trim() ||
      !formData.email.trim() ||
      !formData.baadtype ||
      !formData.maerke.trim() ||
      !formData.pris.trim() ||
      !formData.beskrivelse.trim()
    ) {
      setErrorMessage('Alle felter skal udfyldes.')
      return
    }

    setSubmitting(true)

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          form_type: 'Forespørgsel om båd',
          ...formData,
        }),
      })

      if (response.ok) {
        setSubmitted(true)
      } else {
        setErrorMessage('Der opstod en fejl ved afsendelsen. Prøv venligst igen eller ring til os på 40 20 70 34.')
      }
    } catch (err) {
      setErrorMessage('Der opstod en netværksfejl. Prøv venligst igen eller ring på tlf. 40 20 70 34.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="bg-slate-50 min-h-screen py-12">
      <SEO
        title="Forespørgsel - Nordfyn Marine"
        description="Send en forespørgsel på specifikke bådønsker eller rådgivning hos Nordfyn Marine."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Left Main Column: Header & Form */}
          <div className="lg:col-span-8 space-y-8">
            {/* Header Section */}
            <div className="bg-[#071320] text-white p-8 sm:p-10 border border-slate-800 rounded-2xl shadow-xl relative overflow-hidden">
              <div className="relative z-10 space-y-4">
                <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#2BABE3]">
                  Bådønsker & Søgning
                </span>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light text-white uppercase tracking-wider">
                  Forespørgsel
                </h1>
                <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
                  Har du et ønske om en bestemt båd du søger, så udfyld gerne kontaktformularen og vi kontakter dig hvis vi har mulighed for at hjælpe med at finde drømmebåden.
                </p>
                <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
                  Vi har stort netværk i Europa og samarbejder med mange mæglere i mange lande.
                </p>
              </div>
            </div>

            {submitted ? (
              <div className="bg-white p-8 sm:p-12 border border-slate-200 text-center space-y-4 shadow-sm rounded-2xl">
                <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto text-2xl border border-emerald-200">
                  <i className="fa-solid fa-check" />
                </div>
                <h2 className="font-serif font-light uppercase tracking-wider text-2xl text-slate-900">
                  Tak for din forespørgsel
                </h2>
                <p className="text-xs text-slate-600 max-w-md mx-auto leading-relaxed">
                  Vi har modtaget din forespørgsel og vender tilbage til dig hurtigst muligt.
                </p>
                <div className="pt-4">
                  <a
                    href="tel:40207034"
                    className="inline-flex items-center gap-2 bg-[#1E7BC1] hover:bg-[#2BABE3] text-white px-8 py-3 text-[11px] font-bold uppercase tracking-[0.2em] transition-all shadow-md rounded-xl"
                  >
                    <i className="fa-solid fa-phone" />
                    <span className="whitespace-nowrap">Ring på 40 20 70 34</span>
                  </a>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white p-6 sm:p-10 border border-slate-200 shadow-sm space-y-6 rounded-2xl"
              >
                {/* Honeypot Field */}
                <div className="sr-only absolute" aria-hidden="true">
                  <label htmlFor="website_url">Website URL</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    tabIndex="-1"
                    autoComplete="off"
                    value={formData.website_url}
                    onChange={handleChange}
                  />
                </div>

                {errorMessage && (
                  <div className="p-4 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium flex items-center gap-3 rounded-xl">
                    <i className="fa-solid fa-circle-exclamation text-base shrink-0" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                <div className="space-y-4">
                  <h3 className="font-serif font-normal text-lg uppercase tracking-wider text-slate-900 border-b border-slate-200 pb-2">
                    Kontaktinformation
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Fornavn og efternavn */}
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600 mb-1">
                        Fornavn og efternavn <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="navn"
                        required
                        value={formData.navn}
                        onChange={handleChange}
                        placeholder="Indtast dit fornavn og efternavn"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1E7BC1]"
                      />
                    </div>

                    {/* E-mail */}
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600 mb-1">
                        E-mailadresse <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Indtast din e-mail"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1E7BC1]"
                      />
                    </div>

                    {/* Adresse */}
                    <div className="sm:col-span-2">
                      <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600 mb-1">
                        Adresse <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="adresse"
                        required
                        value={formData.adresse}
                        onChange={handleChange}
                        placeholder="Indtast din adresse"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1E7BC1]"
                      />
                    </div>

                    {/* Post nr. */}
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600 mb-1">
                        Post nr. <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="postnr"
                        required
                        value={formData.postnr}
                        onChange={handleChange}
                        placeholder="Post nr."
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1E7BC1]"
                      />
                    </div>

                    {/* By */}
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600 mb-1">
                        By <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="by"
                        required
                        value={formData.by}
                        onChange={handleChange}
                        placeholder="By"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1E7BC1]"
                      />
                    </div>

                    {/* Telefonnummer */}
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600 mb-1">
                        Telefonnummer <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="telefon"
                        required
                        value={formData.telefon}
                        onChange={handleChange}
                        placeholder="Indtast telefonnummer"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1E7BC1]"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4 pt-2">
                  <h3 className="font-serif font-normal text-lg uppercase tracking-wider text-slate-900 border-b border-slate-200 pb-2">
                    Specifikationer for båden du søger
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Vælg bådtype */}
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600 mb-1">
                        Bådtype <span className="text-rose-500">*</span>
                      </label>
                      <select
                        name="baadtype"
                        required
                        value={formData.baadtype}
                        onChange={handleChange}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1E7BC1]"
                      >
                        <option value="">- Vælg bådtype -</option>
                        <option value="Sejlbåd">Sejlbåd</option>
                        <option value="Motorbåd">Motorbåd</option>
                        <option value="Motorsejler">Motorsejler</option>
                      </select>
                    </div>

                    {/* Indtast mærke */}
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600 mb-1">
                        Mærke <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="maerke"
                        required
                        value={formData.maerke}
                        onChange={handleChange}
                        placeholder="Indtast mærke"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1E7BC1]"
                      />
                    </div>

                    {/* Indtast pris */}
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600 mb-1">
                        Pris <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="pris"
                        required
                        value={formData.pris}
                        onChange={handleChange}
                        placeholder="Indtast pris"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1E7BC1]"
                      />
                    </div>
                  </div>

                  {/* Skriv din forespørgsel... */}
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600 mb-1">
                      Din forespørgsel <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                      name="beskrivelse"
                      required
                      rows="5"
                      value={formData.beskrivelse}
                      onChange={handleChange}
                      placeholder="Skriv din forespørgsel..."
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1E7BC1]"
                    />
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full sm:w-auto bg-[#1E7BC1] hover:bg-[#2BABE3] text-white px-10 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] transition-all shadow-md flex items-center justify-center gap-2 rounded-xl"
                  >
                    {submitting ? (
                      <>
                        <i className="fa-solid fa-spinner animate-spin" />
                        <span>Sender...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane" />
                        <span>Send forespørgsel</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Right Column Sidebar: Visual Image Cards & Contact Info */}
          <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-32 [@media(min-width:1200px)]:lg:top-44">
            {/* Image Card 1 */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="relative h-64 overflow-hidden group">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/7090fdb0-7f55-4b6b-b1f9-a2c2b33a9494/smuk-luksus-motorbaad-hero.png"
                  alt="Nordfyn Marine bådformidling"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#071320]/80 via-[#071320]/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2BABE3] block mb-1">
                    Landeomspændende Netværk
                  </span>
                  <p className="font-serif font-light text-lg uppercase tracking-wider">
                    Vi finder din næste båd
                  </p>
                </div>
              </div>
              <div className="p-5 space-y-3 bg-white">
                <p className="text-xs text-slate-600 leading-relaxed">
                  Kan du ikke finde præcis den båd du drømmer om i vores aktuelle kartotek? Vi søger aktivt i vores store mæglenetværk i hele Europa.
                </p>
              </div>
            </div>

            {/* Quick Contact & Benefits Card */}
            <div className="bg-[#071320] text-white p-6 rounded-2xl border border-slate-800 space-y-5">
              <h3 className="font-serif font-light text-xl uppercase tracking-wider text-white border-b border-slate-800 pb-3">
                Direkte kontakt
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Du er også altid velkommen til at ringe eller skrive direkte til os for en uforpligtende snak om dine bådønsker.
              </p>
              <div className="space-y-3 pt-1">
                <a
                  href="tel:40207034"
                  className="flex items-center gap-3 p-3 bg-slate-900/80 rounded-xl border border-slate-800 hover:border-[#1E7BC1] transition-all group"
                >
                  <div className="w-9 h-9 bg-[#1E7BC1] text-white rounded-lg flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-phone text-sm" />
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block uppercase tracking-wider">Nordfyn Marine</span>
                    <span className="text-xs font-bold text-white group-hover:text-[#2BABE3] transition-colors whitespace-nowrap">
                      40 20 70 34
                    </span>
                  </div>
                </a>

                <a
                  href="mailto:info@nordfyn-marine.dk"
                  className="flex items-center gap-3 p-3 bg-slate-900/80 rounded-xl border border-slate-800 hover:border-[#1E7BC1] transition-all group"
                >
                  <div className="w-9 h-9 bg-[#1E7BC1] text-white rounded-lg flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-envelope text-sm" />
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 block uppercase tracking-wider">E-mailadresse</span>
                    <span className="text-xs font-bold text-white group-hover:text-[#2BABE3] transition-colors whitespace-nowrap">
                      info@nordfyn-marine.dk
                    </span>
                  </div>
                </a>
              </div>
            </div>

            {/* Image Card 2 */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="relative h-56 overflow-hidden group">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/7090fdb0-7f55-4b6b-b1f9-a2c2b33a9494/sejlbaad.jpg"
                  alt="Sejlbåde og motorbåde formidling"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#071320]/80 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <span className="text-[10px] text-[#2BABE3] font-bold uppercase tracking-[0.2em]">
                    Sikker & Tryg Handel
                  </span>
                  <p className="font-serif font-light text-base uppercase tracking-wider">
                    Gennemskuelig rådgivning
                  </p>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  )
}
